import { useEffect } from 'react';

interface SEOSchemaProps {
  lang: 'pt' | 'en';
}

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export function SEOSchema({ lang }: SEOSchemaProps) {
  useEffect(() => {
    const schemas = [
      // Organization Schema
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'SimplesFlow',
        url: siteUrl,
        logo: `${siteUrl}/vite.svg`,
        description:
          lang === 'pt'
            ? 'SimplesFlow automatiza respostas, follow-up e conversao de leads para clinicas, imobiliarias e negocios em Portugal.'
            : 'SimplesFlow automates lead responses, follow-ups, and conversions for clinics, real estate, and businesses in Portugal.',
        sameAs: [
          'https://linkedin.com',
          'https://x.com',
          'https://instagram.com',
        ],
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'PT',
        },
      },
      // WebPage Schema
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name:
          lang === 'pt'
            ? 'SimplesFlow — Automacao de Leads e Landing Pages em Portugal'
            : 'SimplesFlow — Lead Automation and Landing Pages in Portugal',
        description:
          lang === 'pt'
            ? 'SimplesFlow automatiza respostas, follow-up e conversao de leads para clinicas, imobiliarias e negocios em Portugal. Landing pages que convertem + automacao 24/7.'
            : 'SimplesFlow automates lead responses, follow-ups, and conversions for clinics, real estate, and businesses in Portugal. Converting landing pages + 24/7 automation.',
        url: siteUrl,
        inLanguage: lang === 'pt' ? 'pt-PT' : 'en',
        dateModified: '2026-04-23',
        isPartOf: {
          '@type': 'WebSite',
          name: 'SimplesFlow',
          url: siteUrl,
        },
      },
      // Service Schema
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name:
          lang === 'pt'
            ? 'Automacao de Leads e Landing Pages'
            : 'Lead Automation and Landing Pages',
        provider: {
          '@type': 'Organization',
          name: 'SimplesFlow',
        },
        description:
          lang === 'pt'
            ? 'Automacao completa de respostas a leads, follow-up por email, SMS e WhatsApp, e criacao de landing pages otimizadas para conversao.'
            : 'Complete automation of lead responses, follow-up via email, SMS and WhatsApp, and creation of conversion-optimized landing pages.',
        areaServed: {
          '@type': 'Country',
          name: 'Portugal',
        },
        serviceType:
          lang === 'pt'
            ? 'Marketing Automation, Landing Pages, Lead Management'
            : 'Marketing Automation, Landing Pages, Lead Management',
      },
      // FAQPage Schema
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name:
              lang === 'pt'
                ? 'Quanto tempo demora a implementar?'
                : 'How long does implementation take?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                lang === 'pt'
                  ? 'A landing page fica pronta em 7 a 14 dias, com copy, design e implementacao incluidos. A automacao de follow-up e configurada em paralelo.'
                  : 'The landing page is ready in 7 to 14 days, with copy, design and deployment included. Follow-up automation is configured in parallel.',
            },
          },
          {
            '@type': 'Question',
            name:
              lang === 'pt'
                ? 'A automacao funciona 24/7?'
                : 'Does the automation work 24/7?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                lang === 'pt'
                  ? 'Sim. O sistema responde automaticamente a leads em menos de 2 minutos, 24 horas por dia, 7 dias por semana, por email, SMS e WhatsApp.'
                  : 'Yes. The system automatically responds to leads in under 2 minutes, 24 hours a day, 7 days a week, via email, SMS and WhatsApp.',
            },
          },
          {
            '@type': 'Question',
            name:
              lang === 'pt'
                ? 'Para que tipo de negocios funciona?'
                : 'What types of businesses does it work for?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                lang === 'pt'
                  ? 'Funciona para clinicas dentarias, clinicas de estetica, imobiliarias, e-commerce e qualquer negocio que receba leads online e queira converte-los melhor.'
                  : 'It works for dental clinics, aesthetics clinics, real estate agencies, e-commerce and any business that receives leads online and wants to convert them better.',
            },
          },
        ],
      },
    ];

    // Remove existing schema scripts
    const existing = document.querySelectorAll('script[data-seo-schema]');
    existing.forEach((el) => el.remove());

    // Inject new schema scripts
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-schema]');
      scripts.forEach((el) => el.remove());
    };
  }, [lang]);

  return null;
}